import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Search, ShieldOff, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "@/lib/format";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "late", label: "With Late Ded." },
  { id: "ot", label: "OT > 0" },
  { id: "exempt", label: "Exempted" },
  { id: "admin", label: "Admin only" },
];

export default function PreviewFinalizeModal({ open, onClose, uploadId }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [exemptReason, setExemptReason] = useState("Management approval");
  const [exempting, setExempting] = useState(false);
  // Partial exemption modal state — opens a second-level dialog when HR
  // clicks "Waive" on an employee with late deductions, so they can pick
  // specific dates to exempt instead of blanket-wiping all lates.
  const [partialEmp, setPartialEmp] = useState(null);
  const [partialSelected, setPartialSelected] = useState(new Set());
  const [partialSaving, setPartialSaving] = useState(false);

  const load = async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/attendance/uploads/${uploadId}/preview-finalize`
      );
      setData(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Preview failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, uploadId]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    let r = data.rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) =>
        (x.name || "").toLowerCase().includes(q) ||
        (x.punch_id || "").toLowerCase().includes(q) ||
        (x.employee_code || "").toLowerCase().includes(q)
      );
    }
    if (filter === "late") r = r.filter((x) => (x.late_ded_computed ?? 0) > 0);
    else if (filter === "ot") r = r.filter((x) => (x.ot_hours ?? 0) > 0);
    else if (filter === "exempt") r = r.filter((x) => x.late_exempted);
    else if (filter === "admin")
      r = r.filter((x) => x.category === "white_collar" || x.category === "white_collar_female");
    return r;
  }, [data, search, filter]);

  const openPartialExempt = (row) => {
    if (!isSuperAdmin) {
      toast.error("Only super-admin can override late deductions");
      return;
    }
    // Pre-check checkboxes for dates already exempted.
    const pre = new Set(row.exempted_dates || []);
    // If full blanket exemption (no specific dates) and was exempted →
    // pre-check ALL.
    if (row.late_exempted && !row.is_partial_exempt) {
      (row.half_day_breakdown || []).forEach((b) => pre.add(b.date));
    }
    setPartialSelected(pre);
    setPartialEmp(row);
  };

  const savePartialExempt = async () => {
    if (!partialEmp) return;
    const dates = Array.from(partialSelected);
    const reason = exemptReason?.trim() || "Management approval";
    setPartialSaving(true);
    try {
      if (dates.length === 0) {
        // No dates selected → clear exemption entirely
        await api.post(`/attendance/uploads/${uploadId}/late-exemptions`, {
          employee_ids: [partialEmp.employee_id],
          reason,
          action: "clear",
        });
        toast.success("Exemption cleared");
      } else {
        await api.post(`/attendance/uploads/${uploadId}/late-exemptions`, {
          employee_ids: [partialEmp.employee_id],
          exempt_dates: dates,
          reason,
          action: "set",
        });
        toast.success(`${dates.length} date(s) exempted`);
      }
      setPartialEmp(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Save failed: ${detail}`);
    } finally {
      setPartialSaving(false);
    }
  };

  const toggleDate = (date) => {
    setPartialSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const selectAllDates = () => {
    setPartialSelected(new Set(
      (partialEmp?.half_day_breakdown || []).map((b) => b.date)
    ));
  };

  const clearAllDates = () => setPartialSelected(new Set());

  const [bulkN, setBulkN] = useState("");
  const selectFirstN = () => {
    const n = parseInt(bulkN, 10);
    if (!n || n < 1) return;
    const dates = (partialEmp?.half_day_breakdown || []).slice(0, n).map((b) => b.date);
    setPartialSelected(new Set(dates));
  };

  // Legacy single-click waive — kept around in case action column needs
  // a quick "clear" affordance, but the primary path now opens the
  // partial modal.
  const toggleExempt = openPartialExempt;

  const sum = data?.summary;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="preview-finalize-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                Pre-Finalize Calculation Preview
              </DialogTitle>
              <DialogDescription className="text-xs">
                Forecast of payroll inputs before finalizing the upload.
                No DB writes happen on this screen. Late exemptions are
                immediately persisted (super-admin only).
              </DialogDescription>
            </div>
            <button data-testid="preview-close-btn" onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        {/* Summary strip */}
        {sum && (
          <div className="px-6 py-3 grid grid-cols-2 md:grid-cols-5 gap-2 border-b border-slate-100 bg-slate-50/60">
            <Stat label="Employees" value={sum.total_employees} />
            <Stat label="With Late Ded." value={sum.with_late_ded} tone={sum.with_late_ded > 0 ? "warn" : "default"} />
            <Stat label="Exempted" value={sum.exempted} tone="info" />
            <Stat label="Total Late Ded. (days)" value={sum.total_late_ded_days?.toFixed(2)} tone="bad" />
            <Stat label="Predicted Gross" value={formatINR(sum.total_salary_predicted || 0)} tone="good" />
          </div>
        )}

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="preview-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name / punch ID / code"
              className="pl-9 h-9"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`preview-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-2.5 py-1 text-[11px] rounded-md border transition ${
                  filter === f.id
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {isSuperAdmin && (
            <Input
              data-testid="preview-exempt-reason"
              value={exemptReason}
              onChange={(e) => setExemptReason(e.target.value)}
              placeholder="Exemption reason"
              className="h-9 w-56"
            />
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 py-3">
          {loading ? (
            <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10">No matching rows.</div>
          ) : (
            <table className="w-full text-xs border-collapse" data-testid="preview-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-2 text-left">Employee</th>
                  <th className="px-2 py-2 text-left">Type</th>
                  <th className="px-2 py-2 text-right">P / H / A</th>
                  <th className="px-2 py-2 text-right">Day Cnt</th>
                  <th className="px-2 py-2 text-right">OT hrs</th>
                  <th className="px-2 py-2 text-right">OT Cnt</th>
                  <th className="px-2 py-2 text-right">Late</th>
                  <th className="px-2 py-2 text-right">Late Ded.</th>
                  <th className="px-2 py-2 text-right">EL / PL</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2 text-right">CTC</th>
                  <th className="px-2 py-2 text-right">Salary</th>
                  <th className="px-2 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isAdmin = r.category === "white_collar" || r.category === "white_collar_female";
                  const expanded = expandedEmp === r.employee_id;
                  return (
                    <>
                      <tr key={r.employee_id} className={`border-b border-slate-100 hover:bg-slate-50 ${r.late_exempted ? "bg-blue-50/40" : ""}`}>
                        <td className="px-2 py-2">
                          <div className="font-medium text-slate-900">{r.name}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{r.punch_id}</div>
                        </td>
                        <td className="px-2 py-2">
                          <Badge variant="outline" className={
                            isAdmin ? "bg-blue-50 text-blue-700 border-blue-200" :
                            r.type_label === "Overtime" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-slate-50 text-slate-700"
                          }>
                            {r.type_label}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-[11px]">
                          <span className="text-emerald-700">{r.p}</span>
                          {" / "}<span className="text-blue-700">{r.h}</span>
                          {" / "}<span className="text-red-700">{r.a}</span>
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums font-semibold">{r.day_count?.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-amber-700">{r.ot_hours?.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{r.ot_count?.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {isAdmin ? r.late_count : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {r.late_ded_computed > 0 ? (
                            <button
                              data-testid={`expand-${r.punch_id}`}
                              onClick={() => setExpandedEmp(expanded ? null : r.employee_id)}
                              className={`underline-offset-2 hover:underline ${r.late_exempted ? "text-slate-400 line-through" : "text-red-700 font-semibold"}`}
                            >
                              -{r.late_ded_final.toFixed(2)}
                              {r.late_ded_computed !== r.late_ded_final && (
                                <span className="ml-1 text-[9px] text-slate-400">
                                  (was -{r.late_ded_computed.toFixed(2)})
                                </span>
                              )}
                            </button>
                          ) : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-[10px] text-slate-600">
                          {(r.el_used || 0).toFixed(1)} / {(r.pl_used || 0).toFixed(1)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums font-bold text-slate-900">
                          {r.total_count_predicted?.toFixed(2)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-slate-600">
                          {r.ctc ? formatINR(r.ctc) : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums font-semibold text-emerald-700">
                          {r.salary_predicted ? formatINR(r.salary_predicted) : "—"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {isSuperAdmin && isAdmin && r.late_ded_computed > 0 ? (
                            <button
                              data-testid={`exempt-toggle-${r.punch_id}`}
                              onClick={() => openPartialExempt(r)}
                              disabled={exempting}
                              className={`p-1 rounded text-xs ${
                                r.late_exempted
                                  ? (r.is_partial_exempt
                                      ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                                      : "bg-blue-100 text-blue-700 hover:bg-blue-200")
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}
                              title={r.late_exempted
                                ? (r.is_partial_exempt
                                    ? `${r.exempted_count} of ${r.late_count} dates exempted — click to edit`
                                    : "Fully exempted — click to edit")
                                : "Click to choose which dates to waive"}
                            >
                              {r.late_exempted ? (
                                <span className="flex items-center gap-1">
                                  <ShieldCheck className="h-3.5 w-3.5" />
                                  {r.is_partial_exempt
                                    ? `${r.exempted_count}/${r.late_count}`
                                    : "All"}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1"><ShieldOff className="h-3.5 w-3.5" />Waive</span>
                              )}
                            </button>
                          ) : (
                            <span className="text-slate-300 text-[10px]">—</span>
                          )}
                        </td>
                      </tr>
                      {expanded && r.half_day_breakdown?.length > 0 && (
                        <tr className="bg-red-50/40">
                          <td colSpan={13} className="px-4 py-3 border-b border-red-100">
                            <div className="text-[11px] font-semibold text-red-800 mb-2">
                              Half-day breakdown ({r.half_day_breakdown.length} occurrence{r.half_day_breakdown.length === 1 ? "" : "s"})
                              {r.late_exempted && (
                                <span className="ml-2 text-[10px] text-blue-700">— EXEMPTED by {r.exempted_by_email}: {r.exemption_reason}</span>
                              )}
                            </div>
                            <table className="text-[11px]">
                              <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                                  <th className="pr-4 text-left">Date</th>
                                  <th className="pr-4 text-left">First In</th>
                                  <th className="pr-4 text-left">Reason</th>
                                  <th className="text-right">Deduction</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.half_day_breakdown.map((b, i) => (
                                  <tr key={i}>
                                    <td className="pr-4 font-mono">{b.date} (day {b.day})</td>
                                    <td className="pr-4 font-mono">{b.first_in}</td>
                                    <td className="pr-4">{b.reason}</td>
                                    <td className="text-right text-red-700 font-semibold">-{b.amount}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <div className="text-[10px] text-slate-500">
            Showing {rows.length} of {data?.rows?.length || 0} · Month divisor: {data?.divisor || "—"} days
          </div>
          <Button variant="outline" onClick={onClose} data-testid="preview-done-btn">Done</Button>
        </div>
      </DialogContent>

      {/* Partial exemption — date picker modal */}
      <Dialog open={!!partialEmp} onOpenChange={(v) => !v && setPartialEmp(null)}>
        <DialogContent className="max-w-2xl" data-testid="partial-exempt-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>
              Late exemption — {partialEmp?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {partialEmp?.punch_id} · {partialEmp?.type_label} · {partialEmp?.late_count} delay incidents · current deduction {partialEmp?.late_ded_computed?.toFixed(2)} days. Tick boxes to WAIVE specific dates. Unchecked dates remain deducted.
            </DialogDescription>
          </DialogHeader>

          {/* Bulk actions */}
          <div className="flex items-center gap-2 border-t border-b border-slate-100 py-2">
            <button
              data-testid="partial-select-all"
              onClick={selectAllDates}
              className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Select all
            </button>
            <button
              data-testid="partial-clear-all"
              onClick={clearAllDates}
              className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Clear all
            </button>
            <span className="text-[10px] text-slate-400">·</span>
            <span className="text-[11px] text-slate-600">Select first</span>
            <Input
              data-testid="partial-bulk-n"
              type="number"
              min="1"
              value={bulkN}
              onChange={(e) => setBulkN(e.target.value)}
              className="h-7 w-16 text-xs"
            />
            <button
              data-testid="partial-select-first-n"
              onClick={selectFirstN}
              className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Apply
            </button>
          </div>

          {/* Date list */}
          <div className="max-h-80 overflow-y-auto -mx-1">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-2 text-left w-8">Waive?</th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">First In</th>
                  <th className="px-2 py-2 text-left">Reason</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(partialEmp?.half_day_breakdown || []).map((b) => {
                  const checked = partialSelected.has(b.date);
                  return (
                    <tr
                      key={b.date}
                      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${checked ? "bg-emerald-50/50" : ""}`}
                      onClick={() => toggleDate(b.date)}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          data-testid={`partial-check-${b.date}`}
                          checked={checked}
                          onChange={() => toggleDate(b.date)}
                          className="h-3.5 w-3.5 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-2 py-2 font-mono">{b.date} <span className="text-[10px] text-slate-400">(day {b.day})</span></td>
                      <td className="px-2 py-2 font-mono">{b.first_in}</td>
                      <td className="px-2 py-2 text-slate-700">{b.reason}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${checked ? "text-slate-400 line-through" : "text-red-700 font-semibold"}`}>
                        -{b.amount}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer summary */}
          <div className="border-t border-slate-100 pt-3 space-y-3">
            <div className="bg-slate-50 rounded-md p-3 text-xs">
              <div className="flex justify-between mb-1">
                <span className="text-slate-600">Selected to waive:</span>
                <span className="font-semibold text-emerald-700">
                  {partialSelected.size} of {partialEmp?.half_day_breakdown?.length || 0} = -{(partialSelected.size * 0.5).toFixed(2)} days exempted
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Final late deduction:</span>
                <span className="font-bold text-red-700">
                  -{(((partialEmp?.half_day_breakdown?.length || 0) - partialSelected.size) * 0.5).toFixed(2)} days will be deducted
                </span>
              </div>
            </div>
            <Input
              data-testid="partial-reason"
              value={exemptReason}
              onChange={(e) => setExemptReason(e.target.value)}
              placeholder="Exemption reason (required)"
              className="h-9 text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPartialEmp(null)} data-testid="partial-cancel">
                Cancel
              </Button>
              <Button
                onClick={savePartialExempt}
                disabled={partialSaving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="partial-confirm"
              >
                {partialSaving ? "Saving..." : "Confirm Exemption"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

const Stat = ({ label, value, tone = "default" }) => {
  const t = {
    default: "bg-white border-slate-200 text-slate-900",
    good: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    bad: "bg-red-50 border-red-200 text-red-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${t[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
};
